import type { SystemAnnouncement } from '@forq/contracts';

/**
 * Quittierungen liegen im Browser, nicht in der Datenbank -- bewusst:
 *
 * Eine Tabelle "wer hat was gelesen" waere eine Lesebestaetigung. Sie sieht aus wie ein Nachweis
 * ("die Buchhaltung hat den Hinweis quittiert"), ist aber keiner: geklickt hat der Browser, nicht
 * der Mensch. Solange niemand das braucht, ist die ehrlichere Loesung die kleinere.
 *
 * Der Preis ist ehrlich benannt: wer am Laptop UND am Terminal arbeitet, sieht eine 'once'-Meldung
 * zweimal. Fuer einen Changelog ist das kein Schaden. Wird daraus je ein Nachweis, kommt eine
 * Tabelle -- dann aber mit Absicht, nicht als Nebenwirkung.
 */
const seenKeyPrefix = 'forq.announcement-seen';

/**
 * Die Quittierung haengt an id UND updated_at: aendert der Admin den Text von "Changelog V1.1" zu
 * "Changelog V1.2", ist es eine neue Nachricht und muss wieder erscheinen -- sonst liest sie
 * niemand, der die alte schon weggeklickt hat.
 */
function seenKey(announcement: SystemAnnouncement): string {
  return `${seenKeyPrefix}:${announcement.id}:${announcement.updatedAt}`;
}

export function shouldShowAnnouncement(announcement: SystemAnnouncement | null): boolean {
  if (!announcement) return false;
  if (announcement.repeat === 'every-start') return true;

  try {
    return window.localStorage.getItem(seenKey(announcement)) === null;
  } catch {
    // Kein Storage (privates Fenster, gesperrte Cookies): im Zweifel ZEIGEN. Eine Meldung zu oft
    // ist ein Aergernis, eine Meldung zu wenig ist der Fehler, den wir vermeiden wollen.
    return true;
  }
}

export function rememberAnnouncementSeen(announcement: SystemAnnouncement): void {
  if (announcement.repeat !== 'once') return;
  try {
    window.localStorage.setItem(seenKey(announcement), new Date().toISOString());
    forgetOtherAnnouncements(announcement);
  } catch {
    // Ohne Storage kommt die Meldung beim naechsten Start wieder. Das ist der harmlose Ausgang.
  }
}

/**
 * Alte Quittierungen aufraeumen: sonst waechst localStorage bei jedem Changelog um einen Eintrag,
 * den nie wieder jemand liest.
 */
function forgetOtherAnnouncements(current: SystemAnnouncement): void {
  const keep = seenKey(current);
  const stale: string[] = [];
  for (let index = 0; index < window.localStorage.length; index += 1) {
    const key = window.localStorage.key(index);
    if (key && key.startsWith(`${seenKeyPrefix}:`) && key !== keep) stale.push(key);
  }
  stale.forEach((key) => window.localStorage.removeItem(key));
}
